/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomPagination.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 12-08-2024
 * @description : Reusable pagination component
 */
import {
  Box,
  FormControl,
  MenuItem,
  Pagination,
  PaginationItem,
  Select,
  Typography,
} from "@mui/material";
import React from "react";

function CustomPagination({
  page,
  rowsPerPage,
  onPageChange,
  onPageSizeChange,
  hasMore,
  setHasMore,
  pageSizeOptions = [25, 50, 75, 100],
  totalCount,
}) {
  // Calculate total pages if totalCount is provided, else fallback to old logic
  const totalPages =
    typeof totalCount === "number"
      ? Math.max(1, Math.ceil(totalCount / rowsPerPage))
      : hasMore
      ? page + 1
      : page;
  return (
    <Box sx={styles.container}>
      {/* Page Size label */}
      <Typography variant="body2">Page Size:</Typography>

      {/* Select page size */}
      <FormControl variant="outlined" size="small" style={{ minWidth: 70 }}>
        <Select
          size={"small"}
          variant="standard"
          value={rowsPerPage}
          onChange={onPageSizeChange}
        >
          {pageSizeOptions.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Pagination */}

      <Pagination
        count={hasMore ? page + 1 : page || totalPages}
        page={page}
        onChange={onPageChange}
        renderItem={(item) => (
          <PaginationItem
            {...item}
            style={{
              display:
                item.page === page || item.type !== "page"
                  ? "inline-flex"
                  : "none",
            }}
          />
        )}
        color="primary"
        sx={{
          "& .MuiPaginationItem-root": {
            borderRadius: "50%", // round all buttons
            color: "#26890d", // default text color green
          },
        }}
      />
    </Box>
  );
}

export default CustomPagination;

// Styles
const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
};
